import { useId, useState, type FormEvent } from 'react';
import { endpoints } from '@/api/endpoints';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime } from '@/i18n/format';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { ChannelCard as Card, ChannelOwner } from '@/api/types';
import {
  ATTENTION_KEYS, CHANNEL_FIELDS, KIND_HELP_KEYS, KIND_NAME_KEYS, STATE_TONE,
  buildFields, describeChannelError, isStored, lastFour, precheckFields,
  type ChannelFieldName, type ChannelProblem, type FieldValues,
} from '@/lib/channels';

type Busy = 'save' | 'check' | 'disconnect' | null;
type Notice = 'connected' | 'updated' | 'checkOk' | 'disconnected' | null;

const NOTICE_KEYS: Record<Exclude<Notice, null>, TranslationKey> = {
  connected: 'integrations.done.connected',
  updated: 'integrations.done.updated',
  checkOk: 'integrations.done.checkOk',
  disconnected: 'integrations.done.disconnected',
};

type Props = {
  owner: ChannelOwner;
  card: Card;
  /** The server returned a fresh card for this marketplace. */
  onCard: (card: Card) => void;
  /** The server says this card is out of date (already connected / not connected) - reload the list. */
  onStale: () => void;
};

/**
 * One marketplace for one company. Shows the state exactly as the server
 * reports it: only CONNECTED (a successful live check) is green. The key is
 * typed into a local form, sent once, and wiped from state as soon as the
 * request finishes - success or failure. Never persisted, never echoed back.
 */
export function ChannelCard({ owner, card, onCard, onStale }: Props): JSX.Element {
  const { t, locale } = useLanguage();
  const uid = useId();
  const [formOpen, setFormOpen] = useState(false);
  const [values, setValues] = useState<FieldValues>({});
  const [revealKey, setRevealKey] = useState(false);
  const [warehouseId, setWarehouseId] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [problem, setProblem] = useState<ChannelProblem | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const kind = card.kind;
  const stored = isStored(card);
  const specs = CHANNEL_FIELDS[kind];
  const warehouses = owner.warehouses;
  const noWarehouse = warehouses.length === 0;
  const chooseWarehouse = warehouses.length > 1;
  const headingId = `${uid}-h`;
  const name = t(KIND_NAME_KEYS[kind]);
  const hint = lastFour(card.maskedHint);
  const checkedAt = card.lastCheck?.at ? formatDateTime(card.lastCheck.at, locale) : null;
  const syncAt = card.sync?.lastSuccessAt ? formatDateTime(card.sync.lastSuccessAt, locale) : null;
  const warehouseName = stored && chooseWarehouse ? warehouses.find((w) => w.id === card.warehouseId)?.name : undefined;

  const badgeLabel =
    card.state === 'CONNECTED'
      ? checkedAt
        ? t('integrations.state.CONNECTED', { date: checkedAt })
        : t('integrations.state.CONNECTED_NO_DATE')
      : t(`integrations.state.${card.state}` as TranslationKey);

  function openForm(): void {
    setProblem(null);
    setNotice(null);
    setValues({});
    setRevealKey(false);
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
    setValues({});
    setRevealKey(false);
    setWarehouseId('');
    setProblem(null);
  }

  function setValue(field: ChannelFieldName, value: string): void {
    setValues((v) => ({ ...v, [field]: value }));
  }

  function fail(err: unknown): void {
    const p = describeChannelError(err);
    setProblem(p);
    if (p.stale) onStale();
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setNotice(null);
    const local = precheckFields(kind, values);
    if (local) {
      setProblem({ messageKey: local.messageKey, field: local.field });
      return;
    }
    if (!stored && chooseWarehouse && !warehouseId) {
      setProblem({ messageKey: 'integrations.err.warehouseChoose' });
      return;
    }
    setProblem(null);
    setBusy('save');
    const fields = buildFields(kind, values);
    try {
      if (stored && card.channelId) {
        const res = await endpoints.updateChannelCredentials(card.channelId, fields);
        onCard(res.channel);
        setNotice('updated');
      } else {
        const res = await endpoints.connectChannel({
          kind,
          ownerId: owner.id,
          ...(chooseWarehouse ? { warehouseId } : {}),
          fields,
        });
        onCard(res.channel);
        setNotice('connected');
      }
      closeForm();
    } catch (err) {
      fail(err);
      // The secret never outlives the request; the non-secret ids stay for a quick retry.
      setValues((v) => ({ ...v, apiKey: '' }));
      setRevealKey(false);
    } finally {
      setBusy(null);
    }
  }

  async function check(): Promise<void> {
    if (!card.channelId || busy) return;
    setProblem(null);
    setNotice(null);
    setBusy('check');
    try {
      const res = await endpoints.checkChannel(card.channelId);
      onCard(res.channel);
      if (res.result.ok) setNotice('checkOk');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  }

  async function disconnect(): Promise<void> {
    if (!card.channelId || busy) return;
    if (!window.confirm(t('integrations.disconnectConfirm', { name }))) return;
    setProblem(null);
    setNotice(null);
    setBusy('disconnect');
    try {
      const res = await endpoints.disconnectChannel(card.channelId);
      onCard(res.channel);
      closeForm();
      setNotice('disconnected');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  }

  const statusText = busy ? t(`integrations.busy.${busy}` as TranslationKey) : notice ? t(NOTICE_KEYS[notice]) : '';
  const formProblem = problem && !problem.field ? problem : null;
  const fieldProblem = problem?.field ? problem : null;

  return (
    <section className="card channel-card" aria-labelledby={headingId}>
      <div className="channel-head">
        <h3 id={headingId} className="channel-name">{name}</h3>
        <span className={`badge ${STATE_TONE[card.state]}`}>{badgeLabel}</span>
      </div>

      {card.state === 'NEEDS_ATTENTION' && (
        <p className="channel-attention">{t(ATTENTION_KEYS[card.lastCheck?.detail ?? 'DEGRADED'])}</p>
      )}
      {stored && (
        <ul className="channel-facts">
          {hint && <li>{t('integrations.keyHint', { last4: hint })}</li>}
          {card.state !== 'CONNECTED' && checkedAt && <li>{t('integrations.lastCheck', { date: checkedAt })}</li>}
          {warehouseName && <li>{t('integrations.warehouseLabel', { name: warehouseName })}</li>}
          {syncAt && <li>{t('integrations.lastSync', { date: syncAt })}</li>}
        </ul>
      )}

      <p className="meta channel-help">{t(KIND_HELP_KEYS[kind])}</p>
      <p className="meta">{t('integrations.note.check')}</p>

      <div className="channel-status" role="status" aria-live="polite">{statusText}</div>
      {formProblem && !formOpen && (
        <div className="error-banner" role="alert">{t(formProblem.messageKey)}</div>
      )}

      {formOpen ? (
        <form className="channel-form" onSubmit={(e) => void submit(e)} noValidate aria-label={name}>
          {!stored && chooseWarehouse && (
            <div className="field-row">
              <label htmlFor={`${uid}-wh`}>{t('integrations.field.warehouse')}</label>
              <select id={`${uid}-wh`} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} disabled={busy !== null}>
                <option value="">{t('integrations.field.warehouseChoose')}</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          )}
          {specs.map((spec) => {
            const inputId = `${uid}-${spec.name}`;
            const invalid = fieldProblem?.field === spec.name;
            return (
              <div className="field-row" key={spec.name}>
                <label htmlFor={inputId}>{t(spec.labelKey)}</label>
                <div className="channel-input">
                  <input
                    id={inputId}
                    name={spec.name}
                    type={spec.secret && !revealKey ? 'password' : 'text'}
                    inputMode={spec.digits ? 'numeric' : undefined}
                    autoComplete={spec.secret ? 'new-password' : 'off'}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    value={values[spec.name] ?? ''}
                    onChange={(e) => setValue(spec.name, e.target.value)}
                    disabled={busy !== null}
                    aria-invalid={invalid ? 'true' : undefined}
                    aria-describedby={invalid ? `${inputId}-err` : undefined}
                  />
                  {spec.secret && (
                    <button
                      type="button"
                      className="channel-reveal"
                      aria-pressed={revealKey}
                      onClick={() => setRevealKey((v) => !v)}
                      disabled={busy !== null}
                    >
                      {revealKey ? t('integrations.field.hide') : t('integrations.field.show')}
                    </button>
                  )}
                </div>
                {invalid && fieldProblem && (
                  <div className="field-error" id={`${inputId}-err`} role="alert">{t(fieldProblem.messageKey)}</div>
                )}
              </div>
            );
          })}
          {formProblem && <div className="error-banner" role="alert">{t(formProblem.messageKey)}</div>}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy !== null}>
              {t(stored ? 'integrations.action.saveUpdate' : 'integrations.action.save')}
            </button>
            <button type="button" onClick={closeForm} disabled={busy !== null}>{t('integrations.action.cancel')}</button>
          </div>
        </form>
      ) : (
        <div className="form-actions channel-actions">
          {!stored && (
            <button type="button" onClick={openForm} disabled={noWarehouse || busy !== null}>
              {t('integrations.action.connect')}
            </button>
          )}
          {stored && (
            <>
              <button type="button" onClick={() => void check()} disabled={busy !== null}>{t('integrations.action.check')}</button>
              <button type="button" onClick={openForm} disabled={busy !== null}>{t('integrations.action.update')}</button>
              <button type="button" className="danger" onClick={() => void disconnect()} disabled={busy !== null}>
                {t('integrations.action.disconnect')}
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
